import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { plotLabel } from '@/lib/plotLabel';
import { useSearchParams } from 'react-router-dom';
import { AddButton, LiveScreen, QueryState, Select, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat, type StatSecondary } from '@/components/StatCards';
import { SpecRecordDrawer, type FieldSpec, type FormValues } from '@/components/ChildRecords';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';

export interface RegisterStat<T> {
  key: string;
  label: string;
  tone: 'neutral' | 'info' | 'ok' | 'warn' | 'error';
  match: (row: T) => boolean;
}

/**
 * One register screen: stat cards, a searchable grid, and a drawer to add or edit a record - the
 * same shape for accidents, complaints, consultations, internal audits, trades and monitoring.
 */
export function RegisterScreen<T extends { id: string }>({
  title, subtitle, view, table, screenKey, columns, stats, fields, defaults, validate, searchColumns, addLabel, recordLabel,
  children, orderBy, exportName, toolbar, secondary, recordNoun = 'record', canAdd = true, plotFilter = false, extraTab, moreTabs, filters, fixedValues,
}: {
  /** Written on every new record without being asked: an internal permit is issued by the organisation. */
  fixedValues?: Record<string, string>;
  /** Fixed filters: one register seen through one lens (farmer sales are the trades a farmer sells in). */
  filters?: Record<string, string>;
  /** One more tab on a saved record, beside Approval & lock and Change history (an activity's Labour). */
  extraTab?: (row: T) => { label: string; content: ReactNode };
  /** Further tabs on a saved record, after that one (a harvest's Photos, Labour, Trucks ...). */
  moreTabs?: (row: T) => Array<{ label: string; content: ReactNode }>;
  /**
   * A register kept per plot offers a Plot filter above its grid (owner, 2026-09-27: "add features
   * of selection of farmer plot (not farmer)"). The rows need a `plot_id` column.
   */
  plotFilter?: boolean;
  title: string;
  subtitle: string;
  view: string;
  table: string;
  screenKey: string;
  columns: Array<Column<T>>;
  stats: Array<RegisterStat<T>>;
  fields: FieldSpec[];
  defaults?: FormValues;
  validate?: (form: FormValues) => Record<string, string | undefined>;
  searchColumns: string[];
  addLabel: string;
  /** "accident ACC-000001": for the drawer title and the delete confirmation. */
  recordLabel: (row: T) => string;
  /** Child sections of an open record (FMUs, photographs ...). */
  children?: (row: T) => ReactNode;
  orderBy?: { column: string; ascending?: boolean };
  exportName: string;
  /** What one row is called when several are deleted or downloaded at once: "3 accidents". */
  recordNoun?: string;
  toolbar?: (form: FormValues, setForm: (update: (now: FormValues) => FormValues) => void) => ReactNode;
  /** A second figure on every stat card: area, money, days ... */
  secondary?: StatSecondary<T>;
  /**
   * False for a register whose rows the application creates and people only edit (approval
   * workflows are matched to record types in code). The Add button is not offered at all, rather
   * than offered and refused.
   */
  canAdd?: boolean;
}) {
  const { profile } = useAuth();
  const [stat, setStat] = useState<string | null>(null);
  // ?q= opens the register already searched (Renewals due links here with the record's code).
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  useEffect(() => { const q = params.get('q'); if (q !== null) setSearch(q); }, [params]);
  const [open, setOpen] = useState<T | 'new' | null>(null);
  const mayAdd = can(profile, screenKey, 'create');
  const [plotId, setPlotId] = useState('');
  const plots = useRows<{ id: string; plot_code: string; plot_no: string | null; plot_name: string | null; farmer_name: string | null }>('farm_plots', {
    orderBy: { column: 'plot_code' },
  }, plotFilter);
  const rows = useRows<T>(view, {
    filters: { ...(filters ?? {}), ...(plotFilter ? { plot_id: plotId || undefined } : {}) },
    search: search ? { columns: searchColumns, term: search } : undefined,
    orderBy: orderBy ?? { column: 'created_at', ascending: false },
  });
  const allStats = useMemo(() => [{ key: 'all', label: title, tone: 'neutral' as const, match: () => true }, ...stats], [stats, title]);
  const visible = useMemo(() => applyStat(rows.data ?? [], allStats, stat), [rows.data, allStats, stat]);
  const editing = open && open !== 'new' ? (rows.data ?? []).find((row) => row.id === open.id) ?? open : null;

  return (
    <LiveScreen
      actions={canAdd ? <AddButton disabled={!mayAdd} label={addLabel} onClick={() => setOpen('new')}
                                   title={mayAdd ? undefined : 'Your role cannot add to this register.'} /> : undefined}
      subtitle={subtitle}
      title={title}
    >
      {plotFilter && (
        <div className="toolbar-row">
          <label className="field"><span>Plot</span>
            <Select onChange={(value) => { setPlotId(value); setStat(null); }}
                    options={(plots.data ?? []).map((row) => ({
                      // As every plot dropdown reads (api.fn_plot_options): code, P-number, name - farmer.
                      id: row.id, label: plotLabel(row),
                    }))}
                    placeholder={plots.isLoading ? 'Loading plots...' : 'All plots'} value={plotId} />
          </label>
        </div>
      )}
      <StatCards active={stat} onSelect={setStat} rows={rows.data ?? []} secondary={secondary} stats={allStats} />
      <QueryState empty={`Nothing recorded in the ${title.toLowerCase()} yet.`} query={rows}>
        <DataGrid columns={columns} deleteView={view} exportName={exportName} onRowClick={(row) => setOpen(row)}
                  onSearchChange={setSearch} recordNoun={recordNoun} rows={visible} screenKey={screenKey}
                  search={search} selectedId={editing?.id} />
      </QueryState>
      {open && (
        <SpecRecordDrawer<T>
          defaults={defaults} deleteLabel={editing ? recordLabel(editing) : ''} editing={editing}
          fields={fields} key={editing?.id ?? 'new'} onClose={() => setOpen(null)} screenKey={screenKey}
          subtitle={editing ? undefined : subtitle} table={table} title={editing ? capitalise(recordLabel(editing)) : addLabel}
          extraTab={editing && extraTab ? extraTab(editing) : undefined}
          moreTabs={editing && moreTabs ? moreTabs(editing) : undefined}
          parent={fixedValues} toolbar={toolbar} validate={validate} view={view}
        >
          {editing && children?.(editing)}
        </SpecRecordDrawer>
      )}
    </LiveScreen>
  );
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
